<template>
  <div
    :name="validationName"
    :data-path="`${path}-${rowIndex}-${field.id}`"
    :data-subform-col="field.id"
    :class="{ 'subform-error': isFieldInvalid, 'server-error': isFieldInvalid }"
  >
    <div v-if="isReadView">
      <component :is="component" :field="field" :path="path" :is-subform-field="true"></component>
    </div>
    <div v-else class="sub-form-item-wrapper" :class="subFormItemWrapperClass">
      <label
        class="p-2 ps-0 pe-3"
        :for="`subform-row-${rowIndex}-col-${colId}`"
        :class="{
          required: isRequired,
          'subform-form-grid-label': isSubFormField,
          'text-nowrap': subFormView !== 'Grid',
        }"
      >
        {{ $i18n.t(field.label) }}
      </label>
      <div :id="`subform-row-${rowIndex}-col-${colId}`">
        <component
          :key="`subform-row-${rowIndex}-col-${colId}`"
          :is="component"
          v-bind="props"
          :field="field"
          @form-item-changed="onChange"
          @blur="onBlur($event)"
          @user-overwrite="userOverwrite"
        ></component>
      </div>
      <label :for="field.id" v-if="isInvalid" class="error-block">{{ parsedErrorMessage }}</label>
    </div>
  </div>
</template>

<script>
import { pick, PATH_SEPARATOR } from '../../utils'
import { filter, find, findIndex, get, kebabCase, isEmpty, isUndefined } from 'lodash'
import mixinDate from '../../utils/mixin-date'
import {
  isSelect,
  isTextarea,
  isCheckbox,
  isChoiceList,
  isSlider,
  isInfo,
  isLabel,
  isRating,
  isPaint,
  isSignature,
  isCameras,
  isDate,
  isGps,
  isNumber,
  isRichText,
  toDropDownList,
  isMultiSelect,
  prepareQuery,
  isTaggable,
  isSqlSelect,
} from '../../form'
import { FIELD_PROPS, FIELD_REQUIRED_LEVELS, FIELD_TYPES } from '../../consts'
import { BASE_COMPONENT_BY_TYPE, DEFAULT_BASE_COMPONENT } from '@/components/form/helpers/components'

import EventBus from '../../utils/event-bus'
import { mask } from 'vue-the-mask'

import parseError from '../../utils/mixin-parse-error-message'

const IS_ARRAY_RE = /[[\]]/ //ha tombot string-kent kapunk
const PARAMS_TO_LOCALIZE = ['label', 'errorText', 'warningText']

/**
 * @class SubFormItemStore
 *
 * @description This component renders and handles the state of the fields of a subform
 */
export default {
  mixins: [parseError, mixinDate],
  name: 'SubFormItemStore',
  props: ['path', 'readOnly', 'subFormStoreHidden', 'recalculate', 'isListViewModal', 'rowIndex', 'type'],
  directives: { mask },
  data() {
    return {
      updateKey: 1,
      timeoutId: null,
      field: {},
    }
  },
  computed: {
    componentName() {
      return BASE_COMPONENT_BY_TYPE[this.type] || DEFAULT_BASE_COMPONENT
    },
    component() {
      return () =>
        import(
          `${this.isReadView ? './FormControlReadView' : `./${this.componentName === 'NumberInput' ? 'store-controls' : 'controls'}/${this.componentName}`}`
        )
    },
    props() {
      const props = pick(this.field, [
        'id',
        'name',
        'path',
        'type',
        'value',
        'formattedValue',
        'placeholder',
        'required',
        'multiple',
        'list',
        'maxlength',
        'interval',
        'minlength',
        'decimal',
        'min',
        'max',
        'sqlName',
        'sqlFunction',
        'directInputEntry',
        'listExp',
        'dropdownOnlyOne',
      ])
      props.path = this.path
      props.readOnly = this.isReadOnly
      props.groupReadOnly = this.isReadOnly
      props.isInvalid = this.isFieldInvalid
      props.id = `${this.$uid(props.id)}-${this.rowId}`
      props.name = `${props.name}[${this.rowId}]`
      props.formName = this.form.name

      switch (this.field.type) {
        case FIELD_TYPES.DROPDOWN:
        case FIELD_TYPES.MULTISELECT:
        case FIELD_TYPES.STATUS:
          props.multiple = !!isMultiSelect(this.field)
          props.taggable = !!isTaggable(this.field)
          props.fieldName = null
          props.idForm = this.subFormId
          if (!!isSelect(this.field)) {
            props.fieldName = props.name.replace(/\[\d*\]/g, '')
            props.formData = this.$get('getSubFormData')(this.path)
          }
          props.isInvalid = this.isFieldInvalid
          props.field = this.field
          break
        case FIELD_TYPES.NUMBER:
          props.formats = this.field.formats
          props.type = 'text'
          const decimal = get(this.field, ['formats', 'decimal'])
          if (decimal) {
            props.decimals = parseInt(decimal)
          }
          break
        case FIELD_TYPES.SLIDER:
          props.minValue = Number(get(this.field, 'formats.min', 0))
          props.maxValue = Number(get(this.field, 'formats.max', 100))
          props.disabled = this.isReadOnly
          break
        case FIELD_TYPES.SQL_CHOSEN:
          props.field = this.field
          props.multiselect = !!isMultiSelect(this.field)
          props.mongoId = this.field.relFormId
          props.isInvalid = this.isFieldInvalid
          break
        case FIELD_TYPES.DATE:
        case FIELD_TYPES.DATE_INTERVAL:
        case FIELD_TYPES.DATETIME:
        case FIELD_TYPES.DATETIME_INTERVAL:
        case FIELD_TYPES.TIME:
        case FIELD_TYPES.TIME_INTERVAL:
        case FIELD_TYPES.DATETIME_OLD:
          props.formats = this.formats
          props.minExp = this.minExp
          props.maxExp = this.maxExp
          break
        case FIELD_TYPES.CAMERAS:
          props.subformId = this.subFormId
          props.filter = this.filterCameras
          break
        case FIELD_TYPES.INFO:
          props.disabled = this.isReadOnly
          break
        case FIELD_TYPES.LABEL:
          props.disabled = this.isReadOnly
          props.asModal = false
          break
        case FIELD_TYPES.CHECKBOX:
          props.checked = this.field.value
          props.class = this.isInline ? 'w-100 text-center fix-height' : ''
          break
        case FIELD_TYPES.TEXTAREA:
        case FIELD_TYPES.RICH_TEXT:
          props.rows = 1
          props.type =
            this.field.type === FIELD_TYPES.RICH_TEXT && this.field.formattedText ? props.type : FIELD_TYPES.TEXTAREA
          break
        default:
          if (this.hasMask) props.type = FIELD_TYPES.TEXT
          break
      }

      return props
    },
    subFormItemWrapperClass() {
      return { [kebabCase(this.subFormView)]: this.subFormView, required: this.isRequired }
    },
    fieldByPath() {
      return this.$get('expressionFieldByPath')
    },
    fieldValue: {
      get() {
        return this.field.value
      },
      set(value) {
        this.$dispatch('updatePropByPath', {
          path: this.path,
          prop: 'value',
          value,
          formName: this.form.name,
        })
      },
    },
    fieldFormattedValue: {
      get() {
        return this.field.formattedValue
      },
      set(formattedValue) {
        this.$dispatch('updatePropByPath', {
          path: this.path,
          prop: 'formattedValue',
          value: formattedValue,
        })
      },
    },
    isInvalid() {
      return !isEmpty(this.veeValidationErrors.items.find((error) => error.field === this.validationName))
    },
    isFieldInvalid() {
      const name = `${this.field.name}[${this.rowId}]`
      return filter(this.$get('messages'), { name: name, type: 'error' }).length > 0
    },
    /**
     * @description Visszaadja, hogy az item 'list' típusú-e
     * @returns {boolean}
     */
    isListView() {
      return this.subFormView === 'List' && !this.isListViewModal
    },
    isInline() {
      return this.subFormView === 'FormGrid' || this.subFormView === 'List'
    },
    isReadView() {
      return this.$get('isReadView')
    },
    isRequired() {
      const requiredLevel = get(this.$get('subFormFieldByPath')(this.path), 'validations.required')
      return [FIELD_REQUIRED_LEVELS.WARNING, FIELD_REQUIRED_LEVELS.ERROR].includes(requiredLevel)
    },
    isSubFormField() {
      return this.path.split(PATH_SEPARATOR).length === 6
    },
    mainTableId() {
      return this.rows[this.rowId].items[0].value || ''
    },
    filterCameras() {
      let where = {}
      if (this.mainTableId) {
        where = {
          'foreignKey.mainTableName': this.field.refTable,
          'foreignKey.mainTableID': {
            inq: [Number(this.mainTableId), this.mainTableId.toString()],
          },
          or: [{ 'foreignKey.fieldID': this.field.id }, { 'foreignKey.fieldName': this.field.name }],
        }

        if (this.$get('version')) {
          where['foreignKey.version'] = { inq: [this.$get('version')] }
        } else {
          where.active = true
        }
      } else if (!this.mainTableId && !isEmpty(this.field.value)) {
        where = { id: { $in: Array.isArray(this.field.value) ? this.field.value : [this.field.value] } }
      }

      return isEmpty(where) ? null : JSON.stringify({ where })
    },
    form: {
      get() {
        return this.subFormByPath(this.path).subForm
      },
    },
    subFormByPath() {
      return this.$get('fieldByPath')
    },
    subFormId() {
      return this.subFormByPath(this.path).subIdForm
    },
    subFormView() {
      return this.$get('subformView')
    },
    rows: {
      get() {
        return this.form.rows
      },
    },
    colId() {
      return this.path.split(PATH_SEPARATOR).slice(-1)[0]
    },
    rowId() {
      return this.path.split(PATH_SEPARATOR).splice(-2, 1)[0]
    },
    mainFormFields: {
      get() {
        return this.$get('form').mainFormFields
      },
    },
    storedField() {
      return this.$get('subFormFieldByPath')(this.path)
    },
    userOverwritten: {
      get() {
        return this.field.userOverwritten
      },
      set(userOverwritten) {
        this.$dispatch('updatePropByPath', {
          path: this.path,
          prop: 'userOverWritten',
          value: userOverwritten,
        })
      },
    },
    validationName: {
      get() {
        return `${this.$storeNamespace}-${this.subFormId}-${this.field.name}[${this.rowId}]`
      },
    },
    validations() {
      return get(this.field, 'validations', {})
    },
    parsedErrorMessage() {
      const msg = this.veeValidationErrors.first(this.validationName)
      if (!msg) {
        return null
      } else {
        const messageObj = this.createMessageObject({
          msg,
          name: this.validationName,
          label: this.field.label,
          fieldType: this.field.type,
        })
        Object.keys(messageObj.params).forEach((key) => {
          if (PARAMS_TO_LOCALIZE.includes(key)) {
            messageObj.params[key] = this.$i18n.t(messageObj.params[key])
          }
        })
        return this.$i18n.interpolate(this.$i18n.t(messageObj.text), messageObj.params)
      }
    },
    hasMask() {
      return !!this.mask
    },
    mask() {
      const pattern = get(this.field, 'formats.pattern', null)
      if (pattern && IS_ARRAY_RE.test(pattern)) {
        return JSON.parse(pattern)
      } else {
        return pattern || {}
      }
    },
    isReadOnly() {
      return this.readOnly || this.field.readOnly || this.isListView || this.isReadView
    },
    needDebounce() {
      return get(this.storedField, 'needDebounce', false)
    },
  },
  methods: {
    kebabCase,
    isSelect,
    isTextarea,
    isCheckbox,
    isChoiceList,
    isSqlSelect,
    isDate,
    isSlider,
    isInfo,
    isLabel,
    isRating,
    isPaint,
    isSignature,
    isCameras,
    isRichText,
    isGps,
    isNumber,
    toDropDownList,
    prepareQuery,
    userOverwrite(toggle) {
      this.userOverwritten = toggle
    },
    newFormattedValueFrom($event) {
      let selectedOptions = []
      let newFormattedValue = ''
      if (!isEmpty($event.options) && $event.value !== '') {
        if (Array.isArray($event.value)) {
          selectedOptions = $event.options.filter((option) => $event.value.includes(option.key))
          selectedOptions.forEach((option, idx) => {
            if (idx !== 0) newFormattedValue += ', '
            newFormattedValue += option.value
          })
        } else {
          selectedOptions = $event.options.find((option) => option.key === $event.value)
          newFormattedValue = !isEmpty(selectedOptions) && selectedOptions.value
        }
      }
      return newFormattedValue
    },
    onBlur($event) {
      this.isShowMax = false
      this.$emit('blur', $event)
      this.onValidate($event)
    },
    onChange($event) {
      if (isUndefined($event.value) && !this.needDebounce) return
      this.handleInput($event.value)
      if (isSelect(this.field)) {
        this.fieldFormattedValue = this.newFormattedValueFrom($event)
      }
      this.onValidate($event)
    },
    refreshValidator(validationName) {
      const validationIndex = findIndex(this.$validator._base.fields.items, (item) => item.name === validationName)
      if (validationIndex >= 0) {
        this.$validator.detach(validationName)
        this.$validator.attach({
          name: this.validationName,
          rules: this.validations,
        })
      }
    },
    onValidate($event) {
      if (!isEmpty($event) && this.validationName) {
        this.$nextTick(() => {
          if (
            !isEmpty(
              find(this.$validator._base.fields.items, {
                name: this.validationName,
              })
            )
          ) {
            this.$validator
              .validate(this.validationName, this.field.value)
              .then(() => EventBus.$emit('errors-changed', this.validationName))
          }
        })
      }
    },
    clearTimer() {
      clearTimeout(this.timeoutId)
    },
    handleInput(value) {
      if (this.needDebounce) {
        this.clearTimer()
        this.timeoutId = setTimeout(() => {
          this.fieldValue = value
        }, 1500)
      } else {
        this.fieldValue = value
      }
    },
  },
  mounted() {
    EventBus.$on('frontendValidate', this.onValidate)
    this.$watch(
      () => this.$validator.errors,
      (newValue, oldValue) => {
        EventBus.$emit('errors-changed', newValue)
        EventBus.$off('frontendValidate')
      }
    )
    this.$nextTick(() => this.refreshValidator(`${this.field.name}[${this.rowId}]`))
  },
  beforeDestroy() {
    this.$validator.detach(this.validationName)
    EventBus.$emit('errors-changed', [])
    EventBus.$off('frontendValidate')
    clearTimeout(this.timeoutId)
  },
  watch: {
    validations: {
      handler() {
        this.refreshValidator(this.validationName)
      },
      deep: true,
    },
    readOnly: {
      handler() {
        const requiredErrorLevel = get(
          this.field,
          [FIELD_PROPS.FORMATS, FIELD_PROPS.REQUIRED],
          FIELD_REQUIRED_LEVELS.NONE
        )
        const fieldValidationObj = Object.assign({}, this.field.validations, {
          required: this.readOnly ? FIELD_REQUIRED_LEVELS.NONE : requiredErrorLevel,
        })
        this.$dispatch('updatePropByPath', {
          path: this.path,
          prop: 'validations',
          value: fieldValidationObj,
        })
        this.refreshValidator(this.validationName)
      },
    },
    storedField: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.field = newVal
        }
      },
      deep: true,
      immediate: true,
    },
  },
}
</script>

<style scoped lang="scss">
.fix-height {
  height: 40px;
}
label.required:after {
  content: '*';
  color: $color-not-set;
}
.sub-form-item-wrapper.grid {
  display: grid;
  grid-template-columns: 3fr 9fr;
}
.subform-form-grid-label {
  padding: 10px 0;
  font-weight: 700;
  color: $color-text-regular;
  overflow-wrap: anywhere;
}
</style>
